import React from 'react';
import { useHistory } from 'react-router';
import { BEM } from '@feedfm/feed-util';
import * as yup from 'yup';
import { FormikHelpers, useFormik } from 'formik';
import { useId } from '~/hooks/useId';
import { useSafeMutation } from '~/hooks/useSafeMutation';
import { getGraphQLErrorMessagesUi } from '~/util/errors';
import { UserSelect } from '~/components/UserSelect';
import { Alert, Button, Col, Form, FormCheck, Modal, Spinner } from 'react-bootstrap';
import { CreateApplicationAdmin, CreateApplicationAdminVariables } from '~/graphqlTypes/CreateApplicationAdmin';
import { AccountType, CountryInclusionMode } from '~/graphqlTypes/globalTypes';
import createApplicationAdminQuery from './CreateApplicationAdminMutation.gql';
import './CreateAppModal.scss';

const bem = new BEM( 'ops-create-app-modal', [] );

interface AppFormValues {
  name: string;
  ownerId?: number;
  territories: string[];
}

export interface CreateAppModalProps {
  onHide: () => void;
  show: boolean;
}

// Validation schema using Yup
const validationSchema = yup.object( {
  name: yup.string().required( 'App name is required' ),
  ownerId: yup.number(),
  territories: yup.array().of( yup.string() ),
} );

const initialValues: AppFormValues = {
  name: '',
  territories: ['US'],
};

const territories = ['US', 'CA', 'AU', 'MX', 'GB'];

export const CreateAppModal = ( { onHide, show }: CreateAppModalProps ) => {
  const [createApplicationAdmin, { loading }] = useSafeMutation<
  CreateApplicationAdmin,
  CreateApplicationAdminVariables
  >( createApplicationAdminQuery );
  const history = useHistory();

  const onSubmit = async (
    values: AppFormValues,
    { resetForm, setFieldError, setSubmitting, setStatus }: FormikHelpers<AppFormValues>,
  ) => {
    const { name, ownerId, territories: countryIsoCodes } = values;
    const { data, error } = await createApplicationAdmin( {
      role: 'ADMIN', // Default to ADMIN for now. Might make this user configurable in the future.
      newAccountType: AccountType.Feed,
      countryAvailability: {
        mode: CountryInclusionMode.include,
        countryIsoCodes,
      },
      fields: {
        name,
        ownerId,
      },
    } );

    if ( error ) {
      setStatus( { errors: getGraphQLErrorMessagesUi( error ) } );
      setSubmitting( false );
      return;
    }

    // Handle field-specific errors from the server
    if ( data.createApplicationAdmin.errors?.length ) {
      const serverErrors = data.createApplicationAdmin.errors;

      // Map server errors to form fields
      serverErrors.forEach( ( serverError ) => {
        setFieldError( serverError.key, serverError.message );
      } );

      setSubmitting( false );
      return;
    }

    const newAppId = data?.createApplicationAdmin?.instances?.devAppId;

    if ( newAppId ) {
      setSubmitting( false );
      setStatus( 'Successfully created app. Redirecting to new app page.' );
      resetForm();
      onHide();

      history.push( `/appstation/${newAppId}` );
      return;
    } else {
      setStatus( { errors: getGraphQLErrorMessagesUi( 'Unable to load newly created app.' ) } );
      setSubmitting( false );
      return;
    }
  };

  const { touched, values, errors, status, handleChange, handleSubmit, handleBlur, setStatus, setFieldValue } =
    useFormik( {
      initialValues,
      validationSchema,
      onSubmit,
      enableReinitialize: true,
    } );

  const id = useId();
  const idTag = `${id}-create-app-modal`;

  return (
    <Modal show={ show } onHide={ onHide } data-testid="ops-create-app-modal" className={ BEM.$( bem ) }>
      <Form onSubmit={ handleSubmit }>
        <Modal.Header closeButton>
          <Modal.Title>Create New App</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="danger" show={ !loading && Boolean( status?.errors ) }>
            <Alert.Heading>Error Creating User</Alert.Heading>
            { status?.errors }
          </Alert>

          <Form.Group controlId={ `${idTag}-name` }>
            <Form.Label>App Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={ values.name }
              onChange={ handleChange }
              onBlur={ handleBlur }
              isInvalid={ touched.name && !!errors.name }
              disabled={ loading }
            />
            <Form.Control.Feedback type="invalid">{ errors.name }</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId={ `${idTag}-ownerId` }>
            <Form.Label>Owner</Form.Label>
            <UserSelect onChange={ ( user ) => setFieldValue( 'ownerId', user.id, true ) } />
            { touched.ownerId && errors.ownerId && <div className="invalid-feedback d-block">{ errors.ownerId }</div> }
          </Form.Group>

          <Form.Row>
            <Form.Group as={ Col }>
              <Form.Label>Select Station Territories</Form.Label>
              { territories.map( ( territory ) => (
                <FormCheck
                  key={ territory }
                  type="checkbox"
                  id={ `${idTag}-territory-${territory}` }
                  name="territories"
                  label={ territory }
                  value={ territory }
                  checked={ values.territories.includes( territory ) }
                  onChange={ handleChange }
                  onBlur={ handleBlur }
                />
              ) ) }
              <figcaption>Territories where Select stations will play</figcaption>
              <Form.Control.Feedback type="invalid">{ errors.territories }</Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={ onHide } disabled={ loading }>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={ loading }
            onClick={ () => {
              setStatus( undefined );
            } }
          >
            { loading ? (
              <>
                <Spinner animation="border" size="sm" /> Creating...
              </>
            ) : (
              'Create App'
            ) }
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

CreateAppModal.displayName = 'CreateAppModal';
